import { ClientAction, ServerMessage } from "../../../shared/messageTypes";
import { ConnectionToServer } from "./connectionToServer";
import { Multiplayer } from "../../../shared/constants";

declare global {
	interface Window {
		electronApi?: {
			// Steam availability and identity
			isSteamAvailable: () => Promise<boolean>;
			getSteamId64: () => Promise<string | null>;

			// Steam Lobby API (matchmaking)
			createSteamLobby: (maxMembers?: number, lobbyType?: string) => Promise<string | false>;
			joinSteamLobby: (lobbyId: string) => Promise<boolean>;
			onSteamLobbyJoinRequested: (callback: (data: { lobbyId: string }) => void) => void;
			openSteamInviteDialog: () => Promise<boolean>;
			leaveSteamLobby: () => Promise<boolean>;
			sendSteamLobbyMessage: (message: string) => Promise<boolean>;
			getSteamLobbyMembers: () => Promise<Array<{ name: string, steamId: string }>>;
			getSteamLobbyOwner: () => number;

			// ISteamNetworkingSockets API (P2P networking)
			createListenSocket: (virtualPort: number) => Promise<number | false>;
			closeListenSocket: (virtualPort: number) => Promise<boolean>;
			pollIncomingConnections: (virtualPort: number) => Promise<string | null>;
			connectToPeer: (steamId64: string, virtualPort: number) => Promise<boolean>;
			sendToPeer: (steamId64: string, data: string, sendFlags?: number) => Promise<number>;
			receiveFromPeer: (steamId64: string, maxMessages?: number) => Promise<Array<{ data: string; size: number; senderSteamId: string | null }>>;
			disconnectFromPeer: (steamId64: string) => Promise<boolean>;
			getPeerConnectionInfo: (steamId64: string) => Promise<{ steamId: string; state: number } | null>;
			getConnectedPeers: () => Promise<string[]>;
			startMessagePolling: (steamId64: string, pollIntervalMs?: number) => Promise<boolean>;
			stopMessagePolling: () => Promise<boolean>;
			onSteamNetworkingMessage: (callback: (data: { fromSteamId: string; messages: Array<{ data: string }> }) => void) => void;
		};
	}
}

// Send flags matching steamworks.js
export const SteamSendFlags = {
	Unreliable: 0,
	NoNagle: 1,
	UnreliableNoDelay: 2,
	Reliable: 3,
	ReliableNoNagle: 4
} as const;

// Connection states matching steamworks.js
export const SteamConnectionState = {
	None: 0,
	Connecting: 1,
	FindingRoute: 2,
	Connected: 3,
	ClosedByPeer: 4,
	ProblemDetectedLocally: 5
} as const;

/**
 * SteamDataChannel - Mimics RTCDataChannel interface for Steam networking
 * Allows seamless switching between WebRTC and Steam networking
 *
 * Uses push-based message delivery: the main process polls Steam and pushes
 * messages to the renderer via IPC events, which are immediately buffered.
 * This avoids the one-frame delay of pull-based polling from the game loop.
 */
export class SteamDataChannel {
	private peerSteamId: string;
	private messageListeners: Array<(event: { data: string }) => void> = [];
	private openListeners: Array<() => void> = [];
	private errorListeners: Array<(error: any) => void> = [];
	private closeListeners: Array<(intentional: boolean) => void> = [];
	private isOpen: boolean = false;
	private _readyState: 'connecting' | 'open' | 'closing' | 'closed' = 'connecting';

	// Static registry: routes pushed messages to the right channel by peer Steam ID
	private static channels: Map<string, SteamDataChannel> = new Map();
	private static globalListenerSetup: boolean = false;

	private healthCheckInterval: NodeJS.Timeout | null = null;

	constructor(peerSteamId: string) {
		this.peerSteamId = peerSteamId;
	}

	get readyState(): string {
		return this._readyState;
	}

	get steamId(): string {
		return this.peerSteamId;
	}

	/**
	 * Establish the connection, then start push-based message delivery.
	 * After this resolves, messages arrive via IPC events (no game loop polling needed).
	 */
	async connect(): Promise<void> {
		await this.waitForConnection();

		// Register in static map so the global listener can route messages here
		SteamDataChannel.channels.set(this.peerSteamId, this);
		SteamDataChannel.ensureGlobalListener();

		this.startHeartbeat();

		// Tell the main process to start pushing messages for this peer
		// 8ms interval keeps latency low without flooding IPC
		await window.electronApi?.startMessagePolling(this.peerSteamId, 8);
	}

	/**
	 * Set up the single global IPC listener that routes pushed messages to the
	 * correct SteamDataChannel by peer Steam ID. Called at most once.
	 */
	private static ensureGlobalListener() {
		if (SteamDataChannel.globalListenerSetup) return;
		SteamDataChannel.globalListenerSetup = true;

		window.electronApi?.onSteamNetworkingMessage((data: { fromSteamId: string; messages: Array<{ data: string }> }) => {
			const channel = SteamDataChannel.channels.get(data.fromSteamId);
			if (channel?.isOpen) {
				data.messages.forEach(msg => {
					channel.messageListeners.forEach(listener => listener({ data: msg.data }));
				});
			}
		});
	}

	private async waitForConnection(timeoutMs: number = Multiplayer.SteamChannelConnectTimeout, pollIntervalMs: number = 100): Promise<void> {
		const startTime = Date.now();

		return new Promise((resolve, reject) => {
			const checkConnection = async () => {
				try {
					const info = await window.electronApi?.getPeerConnectionInfo(this.peerSteamId);

					if (info && info.state === SteamConnectionState.Connected) {
						this.isOpen = true;
						this._readyState = 'open';
						this.openListeners.forEach(listener => listener());
						resolve();
						return;
					}

					if (Date.now() - startTime > timeoutMs) {
						const error = new Error('Steam connection timeout');
						this.errorListeners.forEach(listener => listener(error));
						reject(error);
						return;
					}

					setTimeout(checkConnection, pollIntervalMs);
				} catch (error) {
					this.errorListeners.forEach(listener => listener(error));
					reject(error);
				}
			};

			checkConnection();
		});
	}

	private startHeartbeat(intervalMs: number = 5000) {
		if (this.healthCheckInterval) return; // Already running

		this.healthCheckInterval = setInterval(async () => {
			try {
				const info = await window.electronApi?.getPeerConnectionInfo(this.peerSteamId);
				if (!info || info.state === SteamConnectionState.ClosedByPeer ||
					info.state === SteamConnectionState.ProblemDetectedLocally ||
					info.state === SteamConnectionState.None
				) {
					console.warn('Steam connection lost, closing channel');
					this.closeFromDrop();
				}
			} catch (error) {
				console.error('Error during Steam health check:', error);
				this.closeFromDrop();
			}
		}, intervalMs);
	}

	/**
	 * Send data to the peer (mimics RTCDataChannel.send)
	 */
	send(data: string): void {
		if (!this.isOpen) {
			console.warn('Attempting to send on non-open Steam channel');
		}
		window.electronApi?.sendToPeer(this.peerSteamId, data, SteamSendFlags.Reliable);
	}

	/**
	 * Add event listener (mimics RTCDataChannel.addEventListener)
	 */
	addEventListener(type: 'message' | 'open' | 'error' | 'close', listener: any): void {
		switch (type) {
			case 'message':
				this.messageListeners.push(listener);
				break;
			case 'open':
				this.openListeners.push(listener);
				// If already open, call immediately
				if (this.isOpen) {
					listener();
				}
				break;
			case 'error':
				this.errorListeners.push(listener);
				break;
			case 'close':
				this.closeListeners.push(listener);
				break;
		}
	}

	/**
	 * Remove event listener
	 */
	removeEventListener(type: 'message' | 'open' | 'error' | 'close', listener: any): void {
		switch (type) {
			case 'message':
				this.messageListeners = this.messageListeners.filter(l => l !== listener);
				break;
			case 'open':
				this.openListeners = this.openListeners.filter(l => l !== listener);
				break;
			case 'error':
				this.errorListeners = this.errorListeners.filter(l => l !== listener);
				break;
			case 'close':
				this.closeListeners = this.closeListeners.filter(l => l !== listener);
				break;
		}
	}

	close(): void {
		const wasOpen = this._readyState !== 'closed';
		this.teardown(); //always, even if already closed: it's idempotent, and reconnect may be mid-flight
		if (wasOpen) {
			this.closeListeners.forEach(listener => listener(true));
		}
	}

	private closeFromDrop(): void {
		// A heartbeat round trip dispatched before a deliberate close() can come back afterwards
		if (this._readyState === 'closed') return;
		this.teardown();
		this.closeListeners.forEach(listener => listener(false));
	}

	private teardown(): void {
		this._readyState = 'closing';
		SteamDataChannel.channels.delete(this.peerSteamId);
		if (this.healthCheckInterval) {
			clearInterval(this.healthCheckInterval);
			this.healthCheckInterval = null;
		}
		window.electronApi?.stopMessagePolling();
		window.electronApi?.disconnectFromPeer(this.peerSteamId);
		this.isOpen = false;
		this._readyState = 'closed';
	}

	// Compatibility properties
	set onmessage(handler: ((event: { data: string }) => void) | null) {
		this.messageListeners = handler ? [handler] : [];
	}

	set onopen(handler: (() => void) | null) {
		this.openListeners = handler ? [handler] : [];
		if (handler && this.isOpen) {
			handler();
		}
	}

	set onerror(handler: ((error: any) => void) | null) {
		this.errorListeners = handler ? [handler] : [];
	}

	set onclose(handler: ((intentional: boolean) => void) | null) {
		this.closeListeners = handler ? [handler] : [];
	}
}

// Utility functions

/**
 * Check if Steam networking is available
 */
export async function isSteamNetworkingAvailable(): Promise<boolean> {
	if (!window.electronApi) return false;
	try {
		const available = await window.electronApi.isSteamAvailable();
		return available === true;
	} catch {
		return false;
	}
}

/**
 * Get local player's Steam ID
 */
export async function getLocalSteamId64(): Promise<string | null> {
	if (!window.electronApi) return null;
	try {
		return await window.electronApi.getSteamId64();
	} catch {
		return null;
	}
}
